import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    Grid,
    Typography,
    Paper,
    List,
    ListItem,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    Collapse,
    IconButton
} from "@mui/material";
import CustomModal from "./CustomModal";
import BarChartIcon from "@mui/icons-material/BarChart";
import { DynamicIcon } from "../utilities/iconUtils";
import { DataGridPro } from "@mui/x-data-grid-pro";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import LinearProgress from '@mui/material/LinearProgress';
import {
    evaluateField,
    createHeaders,
    createRows,
} from "../methods";
import { GetDistinctColumnValues } from "../services";
import { useSelector } from "react-redux";
import Search from "./Search";
import { v4 as uuidv4 } from 'uuid';
import { getCategoryColor } from "../utilities/ColorGenerator";
import { dataGridSx } from "../themes/dataGridSx";

const CategoryModal = ({ selectedColumn, selectedRows, setSelectedRows, filters, setFilters, filterModalOpen, handleCloseFilterModal, setEndingLabel, setResultType, metadata, setSnackbarInfo, loading, setLoading, groupCombinator, setGroupCombinator, showModifyCategory, categoryId, rules, category }) => {
    const [count, setCount] = useState(0);
    const [headers, setHeaders] = useState([]);
    const [data, setData] = useState({
        rows: []
    });
    const [nextData, setNextData] = useState([]);
    const [gridLoading, setGridLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [visibilityModel, setVisibilityModel] = useState({});
    const [distinctJson, setDistinctJson] = useState({});
    const [searchValue, setSearchValue] = useState('');
    const [selection, setSelection] = useState({});
    const [dropdownValue, setDropdownValue] = useState();
    const [useCustomSelection, setUseCustomSelection] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [medicationFamilyExpanded, setMedicationFamilyExpanded] = useState(false);
    const mounted = useRef(true);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const requestKeyRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        return () => {
            mounted.current = true;
        };
    }, []);

    useEffect(() => {
        if (filterModalOpen && selectedColumn) {
            // Reset internal state for new category
            setSearchValue('');
            setSelection({});
            setDropdownValue(category?.columns?.[0]?.key || '');
            setUseCustomSelection(false);
            setSelectedRows([]);
            setPage(1);
            setInitialLoad(true);
        }
    }, [selectedColumn?.column, filterModalOpen]);

    useEffect(() => {
        // Initialize the selection state if it's not set and we have a search value
        if (searchValue && !selection?.value) {
            setSelection({ name: 'contains', value: 'contains' });
        }
    }, [searchValue, selection]);

    useEffect(() => {
        if (selectedColumn?.hiddenColumns) {
            setVisibilityModel(selectedColumn.hiddenColumns);
        }
    }, [selectedColumn]);

    useEffect(() => {
        if (filterModalOpen) {
            setPage(1);
            setInitialLoad(true);
        }
    }, [filterModalOpen, selectedColumn]);

    useEffect(() => {
        // Create a new AbortController instance
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Cleanup function to abort the controller when the component unmounts or re-renders
        return () => {
            abortController.abort();
        };
    }, [selectedColumn, initialLoad, filterModalOpen]);

    useEffect(() => {
        if (initialLoad && filterModalOpen && selectedColumn) {
            // Generate a unique key for the request
            const requestKey = uuidv4();
            requestKeyRef.current = requestKey;

            getDistinctColumnValues('', true, 1, requestKey);
        }
    }, [selectedColumn, initialLoad, filterModalOpen]);

    const clearData = () => {
        setData({ rows: [] });
        setCount(0);
        setNextData([]);
        setHeaders([]);
    }

    const getDistinctColumnValues = async (searchText = '', usePaging = false, page, requestKey = null, filterType = 'contains') => {
        if (page === 1) {
            setData({ rows: [] });
            setCount(0);
            setNextData([]);
        }

        const foundField = rules.find((r) => evaluateField(r.field) === selectedColumn.column);
        if (foundField && mounted.current) {
            const jsonBody = foundField.distinctValueJSON(selectedColumn, searchText);
            jsonBody.page = usePaging ? page : 1;
            jsonBody.filterType = filterType;
            jsonBody.db = metadata.db;
            jsonBody.limit = 25;
            const body = jsonBody;
            setDistinctJson(body);

            setGridLoading(true);

            try {
                const abortController = abortControllerRef.current;
                const res = await GetDistinctColumnValues({ body }, abortController.signal, requestKey);
                if (res && res.success === true) {
                    const apiRes = JSON.parse(res.data);
                    if (apiRes.response?.length === 0) {
                        clearData();
                        return;
                    }

                    const count = apiRes.rowCount;
                    const resData = apiRes.response;

                    const createdRows = createRows(resData, page);
                    const tableHeaders = createHeaders(resData);

                    if (searchText !== '') {
                        setHeaders(tableHeaders);
                        setCount(count);
                        if (usePaging && page !== 1 && resData.length > 0) {
                            // Append new data if it exists and is not already included
                            setNextData(prevData => [...prevData, ...createdRows]);
                        } else {
                            // Reset data for new search or first page
                            setData({ rows: createdRows });
                        }
                    } else {
                        if (page === 1) {
                            setHeaders(tableHeaders);
                            setData({ rows: createdRows });
                            setCount(count);
                        }
                        if (usePaging && page !== 1 && resData.length > 0) {
                            setNextData(prevData => [...prevData, ...createdRows]);
                        }
                    }
                }
            } catch (err) {
                console.error(err);
                setSnackbarInfo({
                    open: true,
                    message: "Request failed, please try again!",
                    severity: "error",
                });
            } finally {
                setGridLoading(false);
                setLoading(false);
                setInitialLoad(false);
            }
        }
    };

    const onRowsSelectionHandler = (selectionModel) => {
        const ids = selectionModel.type === 'include' ? [...selectionModel.ids] : [];
        const selectedRowsData = ids.map((id) => {
            const combinedData = data?.rows.concat(nextData);
            const result = combinedData.find((row) => row.id === id);
            if (result) {
                // find the key that matches selectedColumn.key
                let key = Object.keys(result)?.find(i => i === selectedColumn.key);

                // handling cases where the key doesn't match the actual column name
                if (!key) {
                    const resultKeys = Object.keys(result).filter(k => k !== 'id');
                    // take the first non-id key as the key
                    key = resultKeys[0];
                }

                // Determine which column to use based on dropdown selection
                let fieldColumn = selectedColumn?.column;
                let fieldKey = selectedColumn?.key;
                let fieldFriendlyName = selectedColumn?.friendlyName;

                if (dropdownValue && category?.columns) {
                    const selectedCol = category.columns.find(col => col.key === dropdownValue);
                    if (selectedCol) {
                        fieldColumn = selectedCol.column;
                        fieldKey = selectedCol.key;
                        fieldFriendlyName = selectedCol.friendlyName;
                    }
                }

                const obj = {
                    id: uuidv4(),
                    field: fieldColumn,
                    value: result[key],
                    friendlyName: fieldFriendlyName,
                    operator: selectedColumn?.operator
                };
                return { ...result, ...obj, key: fieldKey, not: false };
            }
        });

        setSelectedRows(selectedRowsData);
    };

    const handleOnRowsScrollEnd = () => {
        if (initialLoad) return;
        if (searchValue === "" && data?.rows.length < 1) {
            setPage(1);
            getDistinctColumnValues('', true, 1);
            return;
        }

        if (!gridLoading && data?.rows.length + nextData.length < count) {
            setPage(prevPage => {
                const nextPage = prevPage + 1;
                getDistinctColumnValues(searchValue, true, nextPage);
                return nextPage;
            });
        }
    };


    const createChildFilterObjects = (childColumnKeys, parentFilter) => {
        if (childColumnKeys.length < 1) return [];

        // Check if we have columns defined in selectedColumn with display properties
        const hasDisplayProperties = selectedColumn?.columns && selectedColumn.columns.some(col => col.primary || col.showInFilter);

        // If we have columns with display properties, only include columns marked with showInFilter
        if (hasDisplayProperties) {
            const filtersToShow = selectedColumn.columns
                .filter(col => col.showInFilter && !col.primary)
                .map(col => {
                    const key = col.key;
                    if (Object.hasOwn(parentFilter, key)) {
                        return {
                            id: uuidv4(),
                            field: col.column,
                            value: parentFilter[key],
                            key: key,
                            friendlyName: col.friendlyName
                        };
                    }
                    return null;
                })
                .filter(item => item !== null)
                .reduce((acc, filter) => {
                    acc.push({ [filter.key]: [filter] });
                    return acc;
                }, []);

            return filtersToShow;
        }

        // Fall back to previous behavior if no display properties
        return childColumnKeys.reduce((acc, col) => {
            if (Object.hasOwn(parentFilter, col)) {
                const filter = {
                    id: uuidv4(),
                    field: col,
                    value: parentFilter[col],
                    key: col,
                    friendlyName: selectedColumn?.childFilters?.find(f => f.key === col)?.friendlyName || col
                };
                acc.push({ [col]: [filter] });
            }
            return acc;
        }, []);
    };

    const handleSubmit = (selectedRows) => {
        let selectionRows = selectedRows;
        if (useCustomSelection) {
            selectionRows = onCustomSelect();
        }

        // Validate rows before processing
        const validRows = selectionRows.filter(row => validateFilterRow(row));

        if (validRows.length === 0) {
            setSnackbarInfo({
                open: true,
                message: "Please select valid filters before submitting.",
                severity: "warning"
            });
            return;
        }

        const childColumnNames = distinctJson.columnNames.filter(col => col.columnName !== selectedColumn.column);
        const childColumnKeys = childColumnNames.map(col => col.columnName.split('.').pop());

        // Process rows with child filters
        const addChildFiltersToRows = (rows) => {
            return rows.map(row => ({
                ...row,
                rows: createChildFilterObjects(childColumnKeys, row)
            }));
        };

        const processedRows = addChildFiltersToRows(validRows);

        // Handle child filter properties
        let isChildFilterNumeric = false;
        if (childColumnKeys.includes('RESULT_TYPE') && processedRows.length > 0) {
            isChildFilterNumeric = processedRows[0]?.RESULT_TYPE === 'N';
        }

        if (selectedColumn.category === "Vital Sign History" && processedRows?.length > 0) {
            setEndingLabel(processedRows[0][childColumnKeys[0]]);
        }

        setFilters((prevState) => {
            const existingCategoryIndex = prevState.findIndex(f => f.category === selectedColumn.friendlyName);

            if (existingCategoryIndex !== -1) {
                const updatedFilters = [...prevState];
                const existingCategory = { ...updatedFilters[existingCategoryIndex] };
                existingCategory.rows = [...existingCategory.rows, ...processedRows];
                if (isChildFilterNumeric) {
                    existingCategory.isChildFilterNumeric = isChildFilterNumeric;
                }
                updatedFilters[existingCategoryIndex] = existingCategory;
                return updatedFilters;
            } else {
                const newCategory = {
                    id: uuidv4(),
                    category: selectedColumn.category,
                    friendlyName: selectedColumn.friendlyName,
                    column: selectedColumn.column,
                    key: selectedColumn.key,
                    rows: processedRows,
                    isChildFilterNumeric: isChildFilterNumeric,
                    combinator: groupCombinator,
                    color: getCategoryColor(selectedColumn.category)
                };
                return [...prevState, newCategory];
            }
        });

        setSelectedRows([]);
        handleCloseFilterModal();
    };

    const handleClose = () => {
        setPage(0);
        setSelectedRows([]);
        handleCloseFilterModal();
        setDropdownValue(null)
    };

    const suffixes = [
        {
            text: 'Ace Inhibitors: pril',
            value: 'pril'
        },
        {
            text: 'Angiotensin Receptors: sartan',
            value: 'sartan'
        },
        {
            text: 'Antiemetics: azine',
            value: 'azine'
        },
        {
            text: 'Antihistamines: ine',
            value: 'ine'
        },
        {
            text: 'Anti-ulcers: prazole',
            value: 'prazole'
        },
        {
            text: 'Antivirals: vir',
            value: 'vir'
        },
        {
            text: 'Beta-Blockers: lol',
            value: 'lol'
        },
        {
            text: 'Bronchodilators: terol',
            value: 'terol'
        },
        {
            text: 'Bronchodilators: phylline',
            value: 'phylline'
        },
        {
            text: 'Calcium Channel Blockers: dipine',
            value: 'dipine'
        },
        {
            text: 'Corticosteroids: sone',
            value: 'sone'
        },
        {
            text: 'Corticosteroids: lone',
            value: 'lone'
        },
        {
            text: 'Local Anesthetics: caine',
            value: 'caine'
        },
        {
            text: 'Potassium-Sparring Diuretics: actone',
            value: 'actone'
        },
        {
            text: 'SGLT2 Inhibitors: flozin',
            value: 'flozin'
        },
        {
            text: 'Statins/Antilipidemics: statin',
            value: 'statin'
        },
        {
            text: 'Thrombnolytics: ace',
            value: 'ace'
        },
    ]

    const handleMedicationSelection = (value) => {
        setSearchValue(value);
        getDistinctColumnValues(value, false, 1, null, 'endsWith')
    }

    const showCustomerDirections = () => {
        if (selectedColumn?.category === "Medication History")
            return (
                <Box sx={{ padding: theme.spacing(1.5, 2, 2) }}>
                    {/* Accordion header */}
                    <Box sx={{
                        padding: theme.spacing(1, 1.5),
                        border: `1px solid ${theme.palette.primary.main}`,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? `${theme.palette.primary.main}15`
                            : `${theme.palette.primary.main}08`,
                        borderRadius: medicationFamilyExpanded ? '8px 8px 0 0' : 2,
                        mb: medicationFamilyExpanded ? 0 : theme.spacing(2),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? `${theme.palette.primary.main}20`
                                : `${theme.palette.primary.main}12`,
                        }
                    }}
                    onClick={() => setMedicationFamilyExpanded(!medicationFamilyExpanded)}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1)
                        }}>
                            <DynamicIcon
                                name="Info"
                                size={16}
                                style={{
                                    color: theme.palette.primary.main,
                                    flexShrink: 0
                                }}
                            />
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                Quick Search by Medication Family
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            sx={{
                                color: theme.palette.primary.main,
                                padding: theme.spacing(0.5),
                                transition: 'transform 0.2s ease',
                                transform: medicationFamilyExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                            }}
                        >
                            <DynamicIcon name="ChevronDown" size={16} />
                        </IconButton>
                    </Box>

                    {/* Collapsible content */}
                    <Collapse in={medicationFamilyExpanded}>
                        <Box sx={{
                            padding: theme.spacing(1.5),
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderTop: 'none',
                            backgroundColor: theme.palette.mode === 'dark'
                                ? `${theme.palette.primary.main}08`
                                : `${theme.palette.primary.main}04`,
                            borderRadius: '0 0 8px 8px',
                            mb: theme.spacing(2)
                        }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: '0.8rem',
                                    lineHeight: 1.4,
                                    mb: theme.spacing(1.5)
                                }}
                            >
                                {`Click any suffix below to search that medication category with "ends with" filter.`}
                            </Typography>

                            {/* Compact medication family pills */}
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: theme.spacing(0.75)
                            }}>
                                {suffixes.map((item, key) => (
                                    <Button
                                        key={key}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleMedicationSelection(item.value)}
                                        sx={{
                                            borderRadius: '16px',
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            padding: theme.spacing(0.25, 1),
                                            minHeight: '28px',
                                            height: '28px',
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                            color: theme.palette.text.primary,
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                borderColor: theme.palette.primary.main,
                                                transform: 'scale(1.02)',
                                                boxShadow: `0 2px 4px ${theme.palette.mode === 'dark'
                                                    ? 'rgba(0, 0, 0, 0.3)'
                                                    : 'rgba(0, 0, 0, 0.15)'}`
                                            },
                                            '&:active': {
                                                transform: 'scale(0.98)'
                                            }
                                        }}
                                    >
                                        {item.text}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
            )
    }


    const onCustomSelect = () => {
        let obj;
        let colSplit;

        if (searchValue?.length > 0) {
            // Check if we have a dropdown value
            if (dropdownValue) {
                const col = category?.columns?.find(column => column.key === dropdownValue);

                if (col) {
                    colSplit = col.column.split(".");
                    obj = {
                        id: uuidv4(),
                        field: col.column,
                        value: searchValue,
                        friendlyName: col.friendlyName,
                        operator: selection?.value || "contains",
                        key: colSplit[colSplit.length - 1],
                        rows: []
                    };
                }
            } else {
                // If no dropdown value, use the selectedColumn
                colSplit = selectedColumn?.column.split(".");
                obj = {
                    id: uuidv4(),
                    field: selectedColumn ? selectedColumn.column : "",
                    value: searchValue,
                    friendlyName: selectedColumn?.friendlyName,
                    operator: selection?.value || "contains",
                    key: colSplit ? colSplit[colSplit.length - 1] : "",
                    rows: []
                };
            }
        } else {
            colSplit = selectedColumn?.column.split(".");

            obj = {
                id: uuidv4(),
                field: selectedColumn ? selectedColumn.column : "",
                value: "*",
                friendlyName: selectedColumn?.friendlyName,
                operator: "contains",
                key: colSplit ? colSplit[colSplit.length - 1] : "",
                rows: []
            };
        }

        const rows = [obj];
        setSelectedRows(rows);
        return rows;
    }

    const handleDropdownChange = (e) => {
        setDropdownValue(e.target.value);
    }

    const validateFilterRow = (row) => {
        return row &&
            row.id &&
            row.field &&
            row.value !== undefined &&
            row.friendlyName &&
            row.operator &&
            row.key;
    };

    const columnDropdown = () => {
        if (category?.columns) {
            return (
                <FormControl style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: '8px' }}>
                    <Select
                        labelId="column-select-label"
                        id="column-select"
                        value={dropdownValue || category?.columns?.[0]?.key}
                        label="Select Column"
                        variant="standard"
                        color="primary"
                        style={{
                            color: theme.palette.primary.main,
                        }}
                        open={dropdownOpen}
                        onOpen={() => setDropdownOpen(true)}
                        onClose={() => setDropdownOpen(false)}
                        onChange={(e) => {
                            handleDropdownChange(e);
                            setDropdownOpen(false);
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    backgroundColor: theme.palette.background.paper,
                                    color: theme.palette.text.primary,
                                },
                            },
                        }}
                    >
                        {category?.columns?.map((column) => (
                            <MenuItem
                                key={column.key}
                                value={column.key}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                        backgroundColor: `${theme.palette.primary.main}20`,
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: `${theme.palette.primary.main}30`,
                                    },
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                }}
                            >
                                {column.friendlyName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        }
        else {
            return null;
        }
    }
    const CustomToolbar = ({ searchValue, onCustomSelect }) => {
        const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

        // Initialize dropdown value if not set
        useEffect(() => {
            if (category?.columns?.length > 0 && !dropdownValue) {
                setDropdownValue(category.columns[0].key);
            }
        }, [category]);

        return (
            <Box sx={{
                padding: 1,
                marginBottom: theme.spacing(1),
                backgroundColor: theme.palette.mode === 'dark'
                    ? `${theme.palette.primary.main}15`
                    : `${theme.palette.primary.main}08`,
                borderRadius: 1,
                border: `1px solid ${theme.palette.mode === 'dark'
                    ? `${theme.palette.primary.main}30`
                    : `${theme.palette.primary.main}20`}`,
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1)
            }}>
                <Checkbox
                    {...label}
                    size="small"
                    checked={useCustomSelection}
                    onClick={() => {
                        setUseCustomSelection(!useCustomSelection);
                        setDropdownOpen(!useCustomSelection);
                    }}
                    sx={{
                        color: theme.palette.primary.main,
                        '&.Mui-checked': {
                            color: theme.palette.primary.main,
                        },
                    }}
                />
                <Typography sx={{
                    fontWeight: 500,
                    fontSize: theme.typography.h6,
                    color: theme.palette.text.primary,
                    flex: 1
                }}>
                    {searchValue?.length > 0 ? (
                        <span>
                            Do you want to use {columnDropdown()} {selection?.name || 'contains'} {searchValue}?
                        </span>
                    ) : (
                        `Do you want to select all ${selectedColumn?.category}?`
                    )}
                </Typography>
            </Box>
        )
    }

    const handleReplaceFilter = (selectedRows) => {
        let newSelectedRows = selectedRows;
        if (useCustomSelection) {
            newSelectedRows = onCustomSelect();
        }

        const childColumnNames = distinctJson.columnNames.filter(col => col.columnName !== selectedColumn.column);
        const childColumnKeys = childColumnNames.map(col => col.columnName.split('.').pop());

        const addChildFiltersToRows = (rows) => {
            return rows.map(row => ({
                ...row,
                rows: createChildFilterObjects(childColumnKeys, row)
            }));
        };

        const processedRows = addChildFiltersToRows(newSelectedRows);

        let isChildFilterNumeric = false;
        if (childColumnKeys.includes('RESULT_TYPE') && processedRows.length > 0) {
            isChildFilterNumeric = processedRows[0]?.RESULT_TYPE === 'N';
        }

        if (selectedColumn.category === "Vital Sign History" && processedRows?.length > 0) {
            setEndingLabel(processedRows[0][childColumnKeys[0]]);
        }

        setFilters((prevState) => {
            if (!Array.isArray(prevState)) {
                console.error('Expected prevState to be an array, got:', prevState);
                return prevState;
            }

            const existingFilterIndex = prevState.findIndex(
                (f) => f.category === selectedColumn.category && f.id === categoryId
            );

            if (existingFilterIndex >= 0) {
                // Replace existing filter's rows completely
                const updatedFilters = [...prevState];
                const updatedCategory = {
                    ...updatedFilters[existingFilterIndex],
                    rows: processedRows
                };

                if (isChildFilterNumeric) {
                    updatedCategory.isChildFilterNumeric = isChildFilterNumeric;
                }

                updatedFilters[existingFilterIndex] = updatedCategory;
                return updatedFilters;
            } else {
                // Create new filter if not found
                const newCategory = {
                    id: uuidv4(),
                    category: selectedColumn.category,
                    friendlyName: selectedColumn.friendlyName,
                    column: selectedColumn.column,
                    key: selectedColumn.key,
                    rows: processedRows,
                    isChildFilterNumeric: isChildFilterNumeric,
                    combinator: groupCombinator,
                    color: getCategoryColor(selectedColumn.category)
                };

                return [...prevState, newCategory];
            }
        });

        setSelectedRows([]);
        handleCloseFilterModal();
    };

    const handleAddToExistingFilter = (selectedRows) => {
        let newSelectedRows = selectedRows;
        if (useCustomSelection) {
            newSelectedRows = onCustomSelect();
        }

        const childColumnNames = distinctJson.columnNames.filter(col => col.columnName !== selectedColumn.column);
        const childColumnKeys = childColumnNames.map(col => col.columnName.split('.').pop());

        const addChildFiltersToRows = (rows) => {
            return rows.map(row => ({
                ...row,
                rows: createChildFilterObjects(childColumnKeys, row)
            }));
        };

        const processedRows = addChildFiltersToRows(newSelectedRows);

        let isChildFilterNumeric = false;
        if (childColumnKeys.includes('RESULT_TYPE') && processedRows.length > 0) {
            isChildFilterNumeric = processedRows[0]?.RESULT_TYPE === 'N';
        }

        if (selectedColumn.category === "Vital Sign History" && processedRows?.length > 0) {
            setEndingLabel(processedRows[0][childColumnKeys[0]]);
        }

        setFilters((prevState) => {
            if (!Array.isArray(prevState)) {
                console.error('Expected prevState to be an array, got:', prevState);
                return prevState;
            }

            const existingFilterIndex = prevState.findIndex(
                (f) => f.category === selectedColumn.category && f.id === categoryId
            );

            if (existingFilterIndex >= 0) {
                // Add to existing filter's rows
                const updatedFilters = [...prevState];
                const existingCategory = { ...updatedFilters[existingFilterIndex] };

                // Add each new row individually to maintain undo granularity
                existingCategory.rows = [...existingCategory.rows, ...processedRows];

                if (isChildFilterNumeric) {
                    existingCategory.isChildFilterNumeric = isChildFilterNumeric;
                }

                updatedFilters[existingFilterIndex] = existingCategory;
                return updatedFilters;
            } else {
                // Create new filter if not found
                const newCategory = {
                    id: uuidv4(),
                    category: selectedColumn.category,
                    friendlyName: selectedColumn.friendlyName,
                    column: selectedColumn.column,
                    key: selectedColumn.key,
                    rows: processedRows,
                    isChildFilterNumeric: isChildFilterNumeric,
                    combinator: groupCombinator,
                    color: getCategoryColor(selectedColumn.category)
                };

                return [...prevState, newCategory];
            }
        });

        setSelectedRows([]);
        handleCloseFilterModal();
    };

    const addIndividualRows = (processedRows, existingCategory) => {
        return {
            ...existingCategory,
            rows: [...existingCategory.rows, ...processedRows]
        };
    };

    const showModalButtons = () => {
        if (showModifyCategory) {
            return (
                <>
                    <Button
                        color="primary"
                        variant="contained"
                        sx={{ margin: "5px" }}
                        disabled={selectedRows?.length === 0 && !useCustomSelection}
                        onClick={() => handleReplaceFilter(selectedRows)}
                    >
                        Replace Existing
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        sx={{ margin: "5px" }}
                        disabled={selectedRows?.length === 0 && !useCustomSelection}
                        onClick={() => handleAddToExistingFilter(selectedRows)}
                    >
                        Add To Existing
                    </Button>
                </>
            )

        } else {
            return (
                <Button
                    color="primary"
                    variant="contained"
                    sx={{ margin: "5px" }}
                    disabled={selectedRows?.length === 0 && !useCustomSelection}
                    onClick={() => handleSubmit(selectedRows)}
                >
                    Add New Filter
                </Button>
            )
        }
    }
    const modalActions = (
        <>
            <Button
                variant="outlined"
                sx={{
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        backgroundColor: theme.palette.action.hover,
                    },
                }}
                onClick={() => handleClose()}
            >
                Close
            </Button>
            {showModalButtons()}
        </>
    );

    return (
        <CustomModal
            open={filterModalOpen}
            onClose={handleCloseFilterModal}
            title={category?.category || "Filter Selection"}
            subtitle="Search and filter records to add to your query"
            icon={selectedColumn?.icon ? (
                <DynamicIcon
                    name={selectedColumn.icon}
                    size={20}
                    style={{ color: 'white' }}
                />
            ) : (
                <BarChartIcon sx={{ color: 'white', fontSize: 20 }} />
            )}
            maxWidth="lg"
            actions={modalActions}
            contentPadding={true}
        >
            <Box>
                {showCustomerDirections()}

                <Box sx={{ mb: theme.spacing(1) }}>
                    <Search
                        getDistinctColumnValues={getDistinctColumnValues}
                        setPage={setPage}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        setSelection={setSelection}
                    />
                </Box>

                <Box sx={{ height: 500, width: '100%' }}>
                    {rules?.length > 0 && data?.rows &&
                        <>
                            <CustomToolbar searchValue={searchValue} onCustomSelect={() => onCustomSelect()} />
                            <DataGridPro
                                rows={JSON.parse(JSON.stringify(data?.rows.concat(nextData)).replaceAll('[char(10)]', '').replaceAll('[char(13)]', '').replaceAll(/\[Ident\d+\]/gmi, ''))}
                                columns={headers}
                                loading={gridLoading}
                                rowCount={count}
                                checkboxSelection={!useCustomSelection}
                                disableRowSelectionOnClick={useCustomSelection}
                                columnVisibilityModel={visibilityModel}
                                hideFooterPagination
                                onRowSelectionModelChange={(ids) => onRowsSelectionHandler(ids)}
                                onRowsScrollEnd={() => handleOnRowsScrollEnd()}
                                slots={{
                                    loadingOverlay: LinearProgress,
                                }}
                                sx={{
                                    ...dataGridSx(theme),
                                    height: '100%',

                                    // Column separators sit on the same border token
                                    // as the cell rules so the grid reads as one mesh.
                                    '& .MuiDataGrid-columnSeparator': {
                                        display: 'flex',
                                        color: theme.palette.surface.border,
                                    },

                                    '& .MuiDataGrid-cell': {
                                        borderBottom: `1px solid ${theme.palette.surface.border}`,
                                        padding: '12px 16px',
                                        fontSize: '0.875rem',
                                        lineHeight: '1.25rem',
                                        color: theme.palette.text.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        '&:focus': {
                                            outline: 'none',
                                        },
                                        '&:focus-within': {
                                            outline: 'none',
                                        },
                                    },

                                    '& .MuiDataGrid-columnHeader': {
                                        backgroundColor: theme.palette.surface.subtle,
                                        '&:focus': {
                                            outline: 'none',
                                        },
                                        '&:focus-within': {
                                            outline: 'none',
                                        },
                                    },

                                    // Checkbox styling
                                    '& .MuiDataGrid-checkboxInput': {
                                        color: theme.palette.primary.main,
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.main,
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '1.25rem',
                                            borderRadius: '4px',
                                        },
                                    },

                                    // Remove default focus outline
                                    '& .MuiDataGrid-main': {
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        '&:focus': {
                                            outline: 'none',
                                        },
                                    },
                                }}
                            />
                        </>
                    }
                </Box>
            </Box>
        </CustomModal>
    );
}


export default CategoryModal;